import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
    Card,
    CardBody,
    Col,
    Row,
    Form,
    Input,
    Label,
    Button,
    Spinner,
} from "reactstrap";
import { toast } from "react-toastify";
import { get, postJson, putJson } from "../../../hook/request";
import Loader from "../../../Components/Widgets/Loader/generic";
import { Btn, H3 } from "../../../AbstractElements";
import Editor from '../../../Components/Forms/EditorText';

type Category = {
    ID: number;
    parentID: string;
    codice: string;
    meta?: string | Record<string, any>;
    children?: Category[];
};

type FormState = {
    codice: string;
    classe: string;
    parentID: string;
    ordine: string;
    abilitato: 1 | 0;
};

type LangFields = {
    nome: string;
    sottotitolo: string;
    descrizione: string;
    permalink: string;
};

export default function CategoryForm() {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const isEdit = useMemo(() => slug && slug !== "new", [slug]);

    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [categories, setCategories] = useState<Category[]>([]);
    const [parentOptions, setParentOptions] = useState<TreeOption[]>([]);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);

    const [form, setForm] = useState<FormState>({
        codice: "",
        classe: "",
        parentID: "",
        ordine: "1",
        abilitato: 1,
    });

    const [lang, setLang] = useState<"it" | "en" | "fr">("it");
    const [langs, setLangs] = useState<Record<"it" | "en" | "fr", LangFields>>({
        it: { nome: "", sottotitolo: "", descrizione: "", permalink: "" },
        en: { nome: "", sottotitolo: "", descrizione: "", permalink: "" },
        fr: { nome: "", sottotitolo: "", descrizione: "", permalink: "" },
    });

    /* ====== Carica lista categorie (parentID) ====== */
    useEffect(() => {
        (async () => {
            try {
                const res = await get<Category[]>('/categories/list');
                const list = (res as any)?.data ?? res;
                setCategories(Array.isArray(list) ? list : []);
            } catch {
                toast.error('Errore nel caricamento categorie');
            }
        })();
    }, []);


    useEffect(() => {
        setParentOptions(buildCategoryOptions(categories, {
            excludeId: isEdit ? slug! : undefined,
            indent: '\u00A0\u00A0\u00A0',   // 3 NBSP per livello
            connector: '└─ ',
        }));
    }, [categories, isEdit, slug]);

    /* ====== Se edit: carico dettaglio categoria ====== */
    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                setLoading(true);
                const res = await get<any>(`/categories/${slug}`);
                const data = (res as any)?.data ?? res;

                setForm({
                    codice: data.codice ?? "",
                    classe: data.classe ?? "",
                    parentID: data.parentID ? String(data.parentID) : "",
                    ordine: String(data.ordine ?? "1"),
                    abilitato: data.abilitato === 1 || data.abilitato === "1" ? 1 : 0,
                });

                // scegli quale campo immagine vuoi gestire (qui: immagineHome)
                const rawImg = data.immagineHome || data.immagineIcon || data.immagineFS || null;
                setExistingImageUrl(rawImg ? String(rawImg) : null);

                const meta = typeof data.meta === "string" ? JSON.parse(data.meta) : (data.meta ?? {});
                setLangs({
                    it: {
                        nome: meta.it?.nome ?? "",
                        sottotitolo: meta.it?.sottotitolo ?? "",
                        descrizione: meta.it?.descrizione ?? "",
                        permalink: meta.it?.permalink ?? "",
                    },
                    en: {
                        nome: meta.en?.nome ?? "",
                        sottotitolo: meta.en?.sottotitolo ?? "",
                        descrizione: meta.en?.descrizione ?? "",
                        permalink: meta.en?.permalink ?? "",
                    },
                    fr: {
                        nome: meta.fr?.nome ?? "",
                        sottotitolo: meta.fr?.sottotitolo ?? "",
                        descrizione: meta.fr?.descrizione ?? "",
                        permalink: meta.fr?.permalink ?? "",
                    },
                });
            } catch {
                toast.error("Errore nel caricamento dettagli categoria");
            } finally {
                setLoading(false);
            }
        })();
    }, [isEdit, slug]);


    /* ====== Gestione input ====== */
    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        setForm((p) => ({
            ...p,
            [name]: type === "number" ? Number(value) : value,
        }));
    };

    const onLangChange = (langKey: keyof typeof langs, field: keyof LangFields, value: string) => {
        
        setLangs((prev) => ({
            ...prev,
            [langKey]: { ...prev[langKey], [field]: value },
        }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] ?? null;
        setImageFile(f);
        if (f) setExistingImageUrl(URL.createObjectURL(f)); // anteprima immediata
    };

    /* ====== Submit ====== */
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const user_email = localStorage.getItem("email") || "supporto@i-factory.biz";
        const fd = new FormData();
        const id = String(slug ?? '');
        fd.append("codice", form.codice);
        fd.append("classe", form.classe);
        fd.append("parentID", form.parentID ? String(form.parentID) : "0");
        fd.append("percorso_file", "");
        fd.append("ordine", form.ordine);
        fd.append("abilitato", String(form.abilitato));
        fd.append("user_email", user_email);
        (["it", "en", "fr"] as const).forEach((lang) => {
            fd.append(`nome_${lang}`, langs[lang].nome);
            fd.append(`sottotitolo_${lang}`, langs[lang].sottotitolo);
            fd.append(`descrizione_${lang}`, langs[lang].descrizione);
            fd.append(`permalink_${lang}`, langs[lang].permalink);
        });
        if (imageFile) fd.append("immagine", imageFile);
        try {
            setSaving(true);
            if (isEdit) {
                fd.append("id", id);
                await putJson(`/categories/edit?ID=${slug}`, fd);
                toast.success("Categoria aggiornata!");
            } else {
                await postJson("/categories/insert", fd);
                toast.success("Categoria creata con successo!");
                navigate("/admin/gestioneDati");
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? "Errore durante il salvataggio");
        } finally {
            setSaving(false);
        }
    };


    /* ====== UI ====== */
    if (loading) return <Loader />;

    return (
        <div className="page-body pt-4">
            <Col xl={12}>
                <Card>
                    <CardBody>
                        <Col xl={12} className="mb-lg-3 d-flex justify-content-between">
                            <H3 className="mb-4">{isEdit ? "Aggiorna Categoria" : "Nuova Categoria"}</H3>
                            <Link to="/admin/gestioneDati">
                                <Btn className="px-4" color="primary" type="button">
                                    Torna Indietro
                                </Btn>
                            </Link>
                        </Col>

                        <Form onSubmit={handleSubmit}>
                            <Row className="g-3">
                                <Col md={6}>
                                    <Label>Codice</Label>
                                    <Input name="codice" value={form.codice} onChange={onChange} />
                                </Col>

                                <Col md={6}>
                                    <Label>Classe</Label>
                                    <Input name="classe" value={form.classe} onChange={onChange} />
                                </Col>

                                <Col md={6}>
                                    <Label htmlFor="parentID">ParentID</Label>
                                    <Input
                                        type="select"
                                        id="parentID"
                                        name="parentID"
                                        value={form.parentID}
                                        onChange={onChange}
                                    >
                                        <option value="">{'Scegli...'}</option>
                                        {parentOptions.map(opt => (
                                            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Input>

                                </Col>

                                <Col md={6}>
                                    <Label>Ordine</Label>
                                    <Input name="ordine" type="number" value={form.ordine} onChange={onChange} />
                                </Col>

                                <Col xs={6}>
                                    <Label htmlFor="formFile">Immagine</Label>
                                    <Input
                                        id="formFile"
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                    />
                                    {existingImageUrl && (
                                        <div className="mt-2">
                                            <img
                                                src={existingImageUrl}
                                                alt="Anteprima immagine"
                                                style={{ maxWidth: 220, borderRadius: 8 }}
                                            />
                                        </div>
                                    )}
                                </Col>



                                <Col md={6}>
                                    <Label>Abilitato</Label>
                                    <div className="d-flex gap-3">
                                        <Label check>
                                            <Input
                                                type="radio"
                                                name="abilitato"
                                                checked={form.abilitato === 1}
                                                onChange={() => setForm((p) => ({ ...p, abilitato: 1 }))}
                                            />{" "}
                                            Sì
                                        </Label>
                                        <Label check>
                                            <Input
                                                type="radio"
                                                name="abilitato"
                                                checked={form.abilitato === 0}
                                                onChange={() => setForm((p) => ({ ...p, abilitato: 0 }))}
                                            />{" "}
                                            No
                                        </Label>
                                    </div>
                                </Col>

                                <hr className="mt-4" />

                                {/* Tabs lingua */}
                                <Col xs={12}>
                                    <ul className="nav nav-tabs">
                                        {(["it", "en", "fr"] as const).map((code) => (
                                            <li className="nav-item" key={code}>
                                                <a
                                                    href="#"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setLang(code);
                                                    }}
                                                    className={`nav-link ${lang === code ? "active" : ""}`}
                                                >
                                                    {code.toUpperCase()}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </Col>

                                {/* Campi lingua */}
                                <Col md={6}>
                                    <Label>Nome {lang.toUpperCase()}</Label>
                                    <Input
                                        value={langs[lang].nome}
                                        onChange={(e) => onLangChange(lang, "nome", e.target.value)}
                                    />
                                </Col>

                                <Col md={6}>
                                    <Label>Sottotitolo {lang.toUpperCase()}</Label>
                                    <Input
                                        value={langs[lang].sottotitolo}
                                        onChange={(e) => onLangChange(lang, "sottotitolo", e.target.value)}
                                    />
                                </Col>

                                <Col xs={12}>
                                    <Label>Descrizione {lang.toUpperCase()}</Label>
                                    <Editor
                                        key={lang}  
                                        value={langs[lang].descrizione}
                                        onChange={(html) => onLangChange(lang, 'descrizione', html)}
                                        height={360}
                                    />
                                    </Col>


                            
                            </Row>

                            <div className="d-flex justify-content-end mt-4">
                                <Button color="primary" type="submit" disabled={saving}>
                                    {saving ? <Spinner size="sm" /> : isEdit ? "Aggiorna" : "Crea"}
                                </Button>
                            </div>
                        </Form>
                    </CardBody>
                </Card>
            </Col>
        </div>
    );
}


type TreeOption = { value: string; label: string; disabled?: boolean };

function getNameFromMeta(meta: unknown, fallback: string = '-'): string {
    if (!meta) return fallback;
    try {
        const obj = typeof meta === 'string' ? JSON.parse(meta) : meta as any;
        return (
            obj?.it?.nome ??
            obj?.IT?.nome ??
            obj?.en?.nome ??
            obj?.EN?.nome ??
            fallback
        );
    } catch {
        return fallback;
    }
}


export function buildCategoryOptions(
    tree: Category[] | undefined,
    opts: { excludeId?: string | number; indent?: string; connector?: string } = {}
): TreeOption[] {
    const indent = opts.indent ?? '\u00A0\u00A0\u00A0'; // NBSP x3
    const connector = opts.connector ?? '└─ ';

    const out: TreeOption[] = [];

    const walk = (nodes: Category[] = [], depth = 0) => {
        nodes.forEach((n) => {
            const id = String(n.ID);
            if (opts.excludeId != null && String(opts.excludeId) === id) {
            } else {
                const base = getNameFromMeta(n.meta, n.codice ?? '-');
                const prefix = depth === 0 ? '' : indent.repeat(depth - 1) + connector;
                out.push({ value: id, label: prefix + base });
            }
            if (Array.isArray(n.children) && n.children.length) {
                walk(n.children, depth + 1);
            }
        });
    };

    walk(Array.isArray(tree) ? tree : [], 0);
    return out;
}
